'use client'

import type React from 'react'
import { use } from 'react'
import { toast } from 'sonner'
import { useCheckout } from '@/lib/hooks'
import { setPaymentMethodType } from '@/lib/redux'

import {
  Button,
  DialogClose,
  Input,
  InputForm,
  Textarea,
} from '@/components/ui'
import { RefContext, type Refs } from '@/components/providers/ref-provider'

interface FuturePaymentDateProps
  extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
}

export const FuturePaymentDate = ({ onClose }: FuturePaymentDateProps) => {
  const { amountInputRef } = use(RefContext) as Refs
  const {
    payment_method,
    setCustomReference,
    setCurrentStep,
    setFuturePaymentDate,
  } = useCheckout()

  const validateInput = (value: string) => {
    if (!value) return false
    const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
    return dateRegex.test(value)
  }

  const handleSubmit = (data: FormData) => {
    const futurePaymentDate = data.get('future-payment-date') as string
    const reference = data.get('reference') as string

    const isFuturePaymentDateRequired = !!payment_method?.future_payment_enabled
    const isReferenceRequired = !!payment_method?.reference_required

    if (isFuturePaymentDateRequired && !validateInput(futurePaymentDate)) {
      toast.error('Please enter a valid date in YYYY-MM-DD format')
      return
    }

    if (isReferenceRequired && !reference) {
      toast.error('Please enter a reference')
      return
    }

    setFuturePaymentDate(futurePaymentDate ?? '')
    setCustomReference(reference ?? '')

    setPaymentMethodType(
      payment_method?.name ?? payment_method?.group ?? 'other',
    )
    setCurrentStep('COLLECT_PAYMENT')
    amountInputRef?.current?.focus()
    onClose?.()
  }

  return (
    <form className="flex w-full flex-col gap-3" action={handleSubmit}>
      {!!payment_method?.future_payment_enabled && (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-gray-950">
            Please enter the date in the format{' '}
            <span className="font-medium">(YYYY-MM-DD)</span>
          </span>
          <Input
            name="future-payment-date"
            type="datepicker"
            placeholder="YYYY-MM-DD"
          />
        </div>
      )}

      {!!payment_method?.reference_required && (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-gray-950">
            Please enter the reference for the payment
          </span>
          <Textarea
            name="reference"
            placeholder="Reference"
            className="resize-none"
          />
        </div>
      )}

      <div className="flex w-full items-center gap-4 pt-3">
        <Button
          type="button"
          size="lg"
          variant="secondary"
          className="w-full"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button type="submit" size="lg" className="w-full">
          Save
        </Button>
      </div>
    </form>
  )
}
